import { usePreferredReducedMotion } from '@vueuse/core';
import {
  computed,
  shallowRef,
  watch,
  onBeforeUnmount,
  type Ref,
  type ShallowRef,
} from 'vue';
import type { EChartsOption } from 'echarts';

const DIMENSION_TIMEOUT_MS = 10_000;

/**
 * ECharts lifecycle composable.
 *
 * - Lazy-imports only the modules needed (tree-shaking friendly).
 * - Auto-resizes with ResizeObserver.
 * - Disposes on unmount.
 */
export function useECharts(
  containerRef: Ref<HTMLElement | null> | ShallowRef<HTMLElement | null>,
  option: Ref<EChartsOption | null>,
) {
  const chartInstance = shallowRef<ReturnType<
    typeof import('echarts/core')['init']
  > | null>(null);
  let resizeObserver: ResizeObserver | null = null;
  let pendingDimensionObserver: ResizeObserver | null = null;
  let disposed = false;
  let initGeneration = 0;

  const reducedMotion = usePreferredReducedMotion();
  const resolvedOption = computed<EChartsOption | null>(() => {
    if (!option.value) return null;
    if (reducedMotion.value !== 'reduce') return option.value;
    const { tooltip } = option.value;
    return {
      ...option.value,
      animation: false,
      ...(tooltip &&
        !Array.isArray(tooltip) && {
          tooltip: { ...tooltip, transitionDuration: 0 },
        }),
    };
  });

  function waitForDimensions(el: HTMLElement): Promise<void> {
    if (el.clientWidth > 0 && el.clientHeight > 0) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        ro.disconnect();
        pendingDimensionObserver = null;
        reject(new Error('Chart container never acquired dimensions'));
      }, DIMENSION_TIMEOUT_MS);

      const ro = new ResizeObserver(() => {
        if (el.clientWidth > 0 && el.clientHeight > 0) {
          clearTimeout(timeout);
          ro.disconnect();
          pendingDimensionObserver = null;
          resolve();
        }
      });
      pendingDimensionObserver = ro;
      ro.observe(el);
    });
  }

  async function init() {
    const generation = ++initGeneration;
    const el = containerRef.value;
    if (!el || disposed) return;

    // Clean up previous observer before re-init
    resizeObserver?.disconnect();
    resizeObserver = null;

    try {
      await waitForDimensions(el);
    } catch {
      return;
    }
    if (disposed || generation !== initGeneration) return;

    const [
      { init: echartsInit, use },
      { BarChart, PieChart },
      {
        TooltipComponent,
        LegendComponent,
        GridComponent,
        AxisPointerComponent,
      },
      { CanvasRenderer },
    ] = await Promise.all([
      import('echarts/core'),
      import('echarts/charts'),
      import('echarts/components'),
      import('echarts/renderers'),
    ]);

    if (disposed || generation !== initGeneration) return;

    // ECharts use() is idempotent — safe to call on every init.
    use([
      BarChart,
      PieChart,
      TooltipComponent,
      LegendComponent,
      GridComponent,
      AxisPointerComponent,

      CanvasRenderer,
    ]);

    if (chartInstance.value) {
      chartInstance.value.dispose();
    }

    const instance = echartsInit(el);
    chartInstance.value = instance;

    if (resolvedOption.value) {
      instance.setOption(resolvedOption.value);
    }

    resizeObserver = new ResizeObserver(() => {
      instance.resize();
    });
    resizeObserver.observe(el);
  }

  watch(
    containerRef,
    (el) => {
      if (el) init();
    },
    { immediate: true },
  );

  watch(resolvedOption, (newOption) => {
    if (chartInstance.value && newOption) {
      chartInstance.value.setOption(newOption, true);
    }
  });

  onBeforeUnmount(() => {
    disposed = true;
    initGeneration++;
    pendingDimensionObserver?.disconnect();
    pendingDimensionObserver = null;
    resizeObserver?.disconnect();
    resizeObserver = null;
    chartInstance.value?.dispose();
    chartInstance.value = null;
  });

  return { chartInstance };
}
